import { z } from 'zod';
import { zAddress, zObjectId } from './primitives.js';
import { zStrategyOverrides } from './strategies.js';
/** Accent colours a tracked wallet can wear (map to theme tokens in the web app). */
export const TRACKED_WALLET_COLORS = ['zinc', 'amber', 'emerald', 'sky', 'violet', 'rose'];
export const zTrackedWalletNickname = z
    .string()
    .trim()
    .min(1, 'Give the wallet a nickname')
    .max(32, 'Keep the nickname under 32 characters');
/** One emoji (a grapheme may be several code points, so bound by length, not count). */
export const zTrackedWalletEmoji = z.string().trim().max(16);
export const zTrackedWalletTag = z.string().trim().min(1).max(24);
/**
 * Quick "SOL per buy" for one wallet: a shortcut for the fixed-size override
 * (`overrides.buy.sizing = { mode: 'fixed', fixedSol }`); `null` removes that override so the
 * strategy sizes the buy again.
 */
export const zTrackedWalletBuySize = z
    .number({ message: 'Enter an amount in SOL' })
    .min(0.001, 'At least 0.001 SOL')
    .max(100, 'At most 100 SOL')
    .nullable();
export const zCreateTrackedWallet = z.object({
    address: zAddress,
    nickname: zTrackedWalletNickname,
    emoji: zTrackedWalletEmoji.optional(),
    color: z.enum(TRACKED_WALLET_COLORS).optional(),
    tags: z.array(zTrackedWalletTag).max(8, 'Up to 8 tags').optional(),
    notes: z.string().trim().max(500, 'Keep notes under 500 characters').optional(),
    /** Copy this wallet's trades (the live feed runs either way). */
    copyEnabled: z.boolean().optional(),
    buySizeSol: zTrackedWalletBuySize.optional(),
});
export const zUpdateTrackedWallet = zCreateTrackedWallet
    .omit({ address: true })
    .partial()
    .extend({ strategyId: zObjectId.nullable().optional() });
/** Bulk import; accepts the common `{ address, name, emoji }` export format too. */
export const zImportTrackedWallets = z.object({
    wallets: z
        .array(z.object({
        address: zAddress,
        nickname: zTrackedWalletNickname.optional(),
        name: z.string().trim().max(64).optional(),
        emoji: zTrackedWalletEmoji.optional(),
        tags: z.array(zTrackedWalletTag).max(8).optional(),
        notes: z.string().trim().max(500).optional(),
    }))
        .min(1, 'Nothing to import')
        .max(500, 'Import at most 500 wallets at once'),
});
/** Behaviour that makes a wallet a poor copy target (computed from its last hour of trades). */
export const LEADER_WARNING_LABEL = {
    high_frequency: 'Trades more than once a minute',
    fast_flipper: 'Sells within seconds of buying',
};
/** Replaces all of a wallet's own settings; `{}` clears them (the wallet follows its strategy). */
export const zUpdateTrackedWalletSettings = z.object({ overrides: zStrategyOverrides });
export const SwapVenue = {
    PumpCurve: 'pump_curve',
    PumpSwap: 'pump_swap',
    Other: 'other',
};
//# sourceMappingURL=tracked-wallets.js.map